import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Flex,
  Text,
  Spinner,
  Badge,
  IconButton,
  TextField,
} from "@radix-ui/themes";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Dialog } from "@/components/shared/dialog";
import { ExploreView } from "@/v2/entities/explore/Explore.model";
import { useExploreApi } from "@/v2/entities/explore/Explore.api";
import { toast } from "sonner";
import RadioGroup from "@/components/shared/inputs/radio-group/RadioGroup";
import styles from "./SubscribeToExploreViewDialog.module.scss";
import { useMutation, useQueryClient } from "react-query";

interface SubscribeToExploreViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: ExploreView;
}

type ReportFrequency = "weekly" | "monthly";

export function SubscribeToExploreViewDialog({
  open,
  onOpenChange,
  view,
}: SubscribeToExploreViewDialogProps) {
  const { subscribeToView } = useExploreApi();
  const queryClient = useQueryClient();
  const [frequency, setFrequency] = useState<ReportFrequency>("weekly");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState<string>("");
  const [errors, setErrors] = useState<{
    emails?: string;
  }>({});

  const frequencyOptions = useMemo(
    () => [
      { value: "weekly", label: "Weekly" },
      { value: "monthly", label: "Monthly" },
    ],
    []
  );

  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  const validateEmails = useCallback(
    (emails: string[]): boolean => {
      if (emails.length === 0) {
        return false;
      }

      const invalidEmails = emails.filter((email) => !emailRegex.test(email));
      return invalidEmails.length === 0;
    },
    [emailRegex]
  );

  const parseEmailsFromInput = useCallback((input: string): string[] => {
    return input
      .split(/[,\n]/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  }, []);

  const addEmails = useCallback(
    (emailsToAdd: string[]) => {
      const validEmails = emailsToAdd.filter((email) => emailRegex.test(email));
      const newEmails = validEmails.filter(
        (email) => !emailList.includes(email)
      );
      if (newEmails.length > 0) {
        setEmailList((prev) => [...prev, ...newEmails]);
      }
      const invalidEmails = emailsToAdd.filter(
        (email) => !emailRegex.test(email)
      );
      if (invalidEmails.length > 0) {
        setErrors({
          emails: `Invalid email${
            invalidEmails.length > 1 ? "s" : ""
          }: ${invalidEmails.join(", ")}`,
        });
      }
    },
    [emailList, emailRegex]
  );

  const removeEmail = useCallback(
    (emailToRemove: string) => {
      setEmailList((prev) => prev.filter((email) => email !== emailToRemove));
      if (errors.emails) {
        setErrors((prev) => ({ ...prev, emails: undefined }));
      }
    },
    [errors.emails]
  );

  const subscribeMutation = useMutation({
    mutationFn: async ({
      viewId,
      frequency,
      subscribersEmails,
    }: {
      viewId: string;
      frequency: ReportFrequency;
      subscribersEmails: string[];
    }) => {
      return subscribeToView(viewId, {
        frequency,
        subscribersEmails,
      });
    },
    onSuccess: () => {
      toast.success("Successfully subscribed to view reports");
      queryClient.invalidateQueries(["explore-view-by-slug", view.slug]);
      setEmailList([]);
      setCurrentInput("");
      setFrequency("weekly");
      setErrors({});
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Failed to subscribe to view reports");
      // eslint-disable-next-line no-console
      console.error("Failed to subscribe:", error);
    },
  });

  const handleSubmit = useCallback(() => {
    const newErrors: { emails?: string } = {};
    let finalEmailList = [...emailList];

    if (currentInput.trim()) {
      const parsedEmails = parseEmailsFromInput(currentInput);
      const validEmails = parsedEmails.filter((email) =>
        emailRegex.test(email)
      );
      finalEmailList = [...finalEmailList, ...validEmails];
      const invalidEmails = parsedEmails.filter(
        (email) => !emailRegex.test(email)
      );
      if (invalidEmails.length > 0) {
        newErrors.emails = `Invalid email${
          invalidEmails.length > 1 ? "s" : ""
        }: ${invalidEmails.join(", ")}`;
      }
    }

    if (finalEmailList.length === 0) {
      newErrors.emails = "At least one email is required";
    } else if (!validateEmails(finalEmailList)) {
      newErrors.emails = "Please enter valid email addresses";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    subscribeMutation.mutate({
      viewId: view.id,
      frequency,
      subscribersEmails: finalEmailList,
    });
  }, [
    emailList,
    currentInput,
    frequency,
    view.id,
    emailRegex,
    parseEmailsFromInput,
    validateEmails,
    subscribeMutation,
  ]);

  const handleCancel = useCallback(() => {
    setEmailList([]);
    setCurrentInput("");
    setFrequency("weekly");
    setErrors({});
    onOpenChange(false);
  }, [onOpenChange]);

  const handleInputChange = useCallback(
    (value: string) => {
      setCurrentInput(value);
      if (errors.emails) {
        setErrors((prev) => ({ ...prev, emails: undefined }));
      }
    },
    [errors.emails]
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        if (currentInput.trim()) {
          const parsedEmails = parseEmailsFromInput(currentInput);
          addEmails(parsedEmails);
          setCurrentInput("");
        }
      }
    },
    [currentInput, parseEmailsFromInput, addEmails]
  );

  const handleInputBlur = useCallback(() => {
    if (currentInput.trim()) {
      const parsedEmails = parseEmailsFromInput(currentInput);
      addEmails(parsedEmails);
      setCurrentInput("");
    }
  }, [currentInput, parseEmailsFromInput, addEmails]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) =>
        !subscribeMutation.isLoading && onOpenChange(open)
      }
    >
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.Close
          className={styles.closeButton}
          disabled={subscribeMutation.isLoading}
        />
        <Flex direction="column" gap="6" p="5">
          <Flex direction="column" gap="1">
            <Text size="4" weight="medium" className={styles.title}>
              Subscribe to Reports
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              Get regular reports for "{view.title}" delivered to your inbox.
            </Text>
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Report Frequency</Text>
            </label>
            <RadioGroup
              value={frequency}
              onChange={(value) => setFrequency(value as ReportFrequency)}
              options={frequencyOptions}
              layout="horizontal"
            />
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Email Addresses</Text>
            </label>
            <Flex
              direction="column"
              gap="2"
              className={styles.emailInputContainer}
            >
              <TextField.Root
                placeholder="email@example.com, another@example.com"
                value={currentInput}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                disabled={subscribeMutation.isLoading}
                size="2"
              />
              {emailList.length > 0 && (
                <Flex gap="2" wrap="wrap">
                  {emailList.map((email) => (
                    <Badge
                      key={email}
                      color="blue"
                      variant="soft"
                      size="2"
                      className={styles.emailChip}
                    >
                      {email}
                      <IconButton
                        color="gray"
                        variant="ghost"
                        highContrast
                        size="1"
                        onClick={() => removeEmail(email)}
                        disabled={subscribeMutation.isLoading}
                        className={styles.removeButton}
                      >
                        <Cross2Icon />
                      </IconButton>
                    </Badge>
                  ))}
                </Flex>
              )}
            </Flex>
            <Text size="1" color="gray">
              Enter email addresses separated by commas or press Enter
            </Text>
            {errors.emails && (
              <Text size="1" color="red">
                {errors.emails}
              </Text>
            )}
          </Flex>

          <Flex gap="3" justify="end">
            <Button
              variant="ghost"
              size="2"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={subscribeMutation.isLoading}
            >
              <Text size="3" color="gray">
                Cancel
              </Text>
            </Button>
            <Button
              variant="solid"
              color="gray"
              size="2"
              highContrast
              onClick={handleSubmit}
              disabled={
                (emailList.length === 0 && !currentInput.trim()) ||
                subscribeMutation.isLoading
              }
              className={styles.subscribeButton}
            >
              {subscribeMutation.isLoading ? (
                <>
                  <Spinner size="1" />
                  <Text size="3">Subscribing...</Text>
                </>
              ) : (
                <Text size="3">Subscribe</Text>
              )}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
